import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  Divider,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ApiService from "../../services/api-service";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import ReorderableFeaturedList from "./ReorderableFeaturedList";
import Avatar from "@mui/material/Avatar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";



import "./Admin.scss";

export default function Featured() {
  const [tab, setTab] = useState(0);

  // Artist state
  const [featuredArtists, setFeaturedArtists] = useState([]);
  const [artistMsg, setArtistMsg] = useState("");
  const [artistId, setArtistId] = useState("");
  const [artistSearch, setArtistSearch] = useState("");
  const [artistSearchResults, setArtistSearchResults] = useState([]);

  // Music state
  const [featuredMusic, setFeaturedMusic] = useState([]);
  const [musicMsg, setMusicMsg] = useState("");
  const [musicId, setMusicId] = useState("");
  const [musicSearch, setMusicSearch] = useState("");
  const [musicSearchResults, setMusicSearchResults] = useState([]);

  // Video state
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [videoMsg, setVideoMsg] = useState("");
  const [videoId, setVideoId] = useState("");
  const [videoSearch, setVideoSearch] = useState("");
  const [videoSearchResults, setVideoSearchResults] = useState([]);

  // Playlist state
  const [featuredPlaylists, setFeaturedPlaylists] = useState([]);
  const [playlistMsg, setPlaylistMsg] = useState("");
  const [playlistId, setPlaylistId] = useState("");
  const [playlistSearch, setPlaylistSearch] = useState("");
  const [playlistSearchResults, setPlaylistSearchResults] = useState([]);

  useEffect(() => {
    if (tab === 0) fetchFeaturedArtists();
    if (tab === 1) fetchFeaturedMusic();
    if (tab === 2) fetchFeaturedVideos();
    if (tab === 3) fetchFeaturedPlaylists();
    // eslint-disable-next-line
  }, [tab]);

  // --- Featured Artists -----------
  const fetchFeaturedArtists = async () => {
    setArtistMsg("");
    try {
      const resp = await ApiService.FEATURE_ARTIST("&pageSize=16");
      setFeaturedArtists(resp.result || []);
    } catch (e) {
      setArtistMsg("Failed to load featured artists.");
    }
  };

const handleArtistSearch = async (value) => {
  setArtistSearchResults([]);
  if (!value) return;
  try {
    const resp = await ApiService.SEARCH_ARTIST(value);
    setArtistSearchResults(resp.result || []);
  } catch (e) {
    setArtistMsg("Artist search failed.");
  }
};

const handleFeatureArtist = async (artistId) => {
  setArtistMsg("");
  try {
    await ApiService.ADMIN_FEATURE_ARTIST({ order: [{ artistId, orderNo: 1 }] });
    setArtistMsg("Artist featured!");
    setArtistId("");
    fetchFeaturedArtists(); // Refresh the featured artists list
  } catch (e) {
    setArtistMsg("Failed to feature artist.");
  }
};

  const handleRemoveFeaturedArtist = async (id) => {
    setArtistMsg("");
    try {
      await ApiService.ADMIN_UNFEATURE_ARTIST({ _id: id });
      setArtistMsg("Artist removed from featured!");
      fetchFeaturedArtists();
    } catch (e) {
      setArtistMsg("Failed to remove artist.");
    }
  };

  // --- Featured Music -----------
  const fetchFeaturedMusic = async () => {
    setMusicMsg("");
    try {
      const resp = await ApiService.FEATURE_MUSIC("&pageSize=16");
      setFeaturedMusic(resp.result || []);
    } catch (e) {
      setMusicMsg("Failed to load featured music.");
    }
  };

  const handleMusicSearch = async () => {
    setMusicSearchResults([]);
    if (!musicSearch) return;
    try {
      const resp = await ApiService.SEARCH_MUSIC({ query: musicSearch });
      setMusicSearchResults(resp.result || []);
    } catch (e) {
      setMusicMsg("Music search failed.");
    }
  };

  const handleFeatureMusic = async () => {
    setMusicMsg("");
    try {
      await ApiService.ADMIN_FEATURE_MUSIC({ order: [{ musicId, orderNo: 1 }] });
      setMusicMsg("Music featured!");
      setMusicId("");
      fetchFeaturedMusic();
    } catch (e) {
      setMusicMsg("Failed to feature music.");
    }
  };

  const handleRemoveFeaturedMusic = async (id) => {
    setMusicMsg("");
    try {
      await ApiService.ADMIN_UNFEATURE_MUSIC({ musicId: id });
      setMusicMsg("Music removed from featured!");
      fetchFeaturedMusic();
    } catch (e) {
      setMusicMsg("Failed to remove music.");
    }
  };

  // --- Featured Videos -----------
  const fetchFeaturedVideos = async () => {
    setVideoMsg("");
    try {
      const resp = await ApiService.FEATURE_VIDEO("pageSize=16");
      console.log("Featured videos response:", resp);
      setFeaturedVideos(resp.result || []);
    } catch (e) {
      console.error("Failed to load featured videos:", e);
      setVideoMsg("Failed to load featured videos.");
    }
  };

  const handleVideoSearch = async () => {
    setVideoSearchResults([]);
    if (!videoSearch) return;
    try {
      const resp = await ApiService.SEARCH_VIDEO({ keyword: videoSearch });
      setVideoSearchResults(resp.result?.videos || []);
    } catch (e) {
      setVideoMsg("Video search failed.");
    }
  };

  const handleFeatureVideo = async () => {
    setVideoMsg("");
    try {
      console.log("Featuring video with ID:", videoId);
      const response = await ApiService.ADMIN_FEATURE_VIDEO({ order: [{ _id: videoId, orderNo: 1 }] });
      console.log("Feature video response:", response);
      setVideoMsg("Video featured!");
      setVideoId("");
      await fetchFeaturedVideos();
    } catch (e) {
      console.error("Failed to feature video:", e);
      setVideoMsg("Failed to feature video.");
    }
  };

  const handleRemoveFeaturedVideo = async (id) => {
    setVideoMsg("");
    try {
      await ApiService.ADMIN_UNFEATURE_VIDEO({ _id: id });
      setVideoMsg("Video removed from featured!");
      fetchFeaturedVideos();
    } catch (e) {
      setVideoMsg("Failed to remove video.");
    }
  };

  // --- Featured Playlists -----------
  const fetchFeaturedPlaylists = async () => {
    setPlaylistMsg("");
    try {
      const resp = await ApiService.FEATURE_PLAYLIST("&pageSize=16");
      setFeaturedPlaylists(resp.result || []);
    } catch (e) {
      setPlaylistMsg("Failed to load featured playlists.");
    }
  };

  const handlePlaylistSearch = async () => {
    setPlaylistSearchResults([]);
    if (!playlistSearch) return;
    try {
      const resp = await ApiService.SEARCH_PLAYLIST(playlistSearch);
      setPlaylistSearchResults(resp.result || []);
    } catch (e) {
      setPlaylistMsg("Playlist search failed.");
    }
  };

  const handleFeaturePlaylist = async () => {
    setPlaylistMsg("");
    try {
      await ApiService.ADMIN_FEATURE_PLAYLIST({ order: [{ playlistId, orderNo: 1 }] });
      setPlaylistMsg("Playlist featured!");
      setPlaylistId("");
      fetchFeaturedPlaylists();
    } catch (e) {
      setPlaylistMsg("Failed to feature playlist.");
    }
  };

  const handleRemoveFeaturedPlaylist = async (id) => {
    setPlaylistMsg("");
    try {
      await ApiService.ADMIN_UNFEATURE_PLAYLIST({ playlistId: id });
      setPlaylistMsg("Playlist removed from featured!");
      fetchFeaturedPlaylists();
    } catch (e) {
      setPlaylistMsg("Failed to remove playlist.");
    }
  };

    // --- Drag-and-Drop Handlers ---
  const handleDragEnd = (result, items, setItems) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(items);
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);

    setItems(reorderedItems);
  };

  const handleSaveOrder = async (items, apiMethod, setMsg) => {
    setMsg("");
    try {
      const order = items.map((item, index) => ({
        id: item._id,
        orderNo: index + 1,
      }));
      await apiMethod({ order });
      setMsg("Order saved successfully!");
    } catch (e) {
      setMsg("Failed to save order.");
    }
  };

  return (
    <Box className="adminPortalContainer">
      <Typography className="adminHeader" variant="h4" gutterBottom>
        Featured
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        className="tabsRoot"
        TabIndicatorProps={{ style: { background: "#1a237e" } }}
      >
        <Tab label="Featured Artists" />
        <Tab label="Featured Music" />
        <Tab label="Featured Videos" />
        <Tab label="Featured Playlists" />
      </Tabs>

      {/* Featured Artist Tab */}
        {tab === 0 && (
        <Box className="tabPanel" sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {/* Left: Currently Featured Artists */}
            <Box flex={1} minWidth={320} maxWidth={480}>
            <Typography className="sectionTitle">Currently Featured Artists</Typography>
            <ReorderableFeaturedList
                items={featuredArtists}
                setItems={setFeaturedArtists}
                onRemove={handleRemoveFeaturedArtist}
                onSaveOrder={async () => {
                try {
                    await ApiService.ADMIN_FEATURE_ARTIST({
                    order: featuredArtists.map((artist, idx) => ({
                        artistId: artist._id,
                        orderNo: idx + 1,
                    })),
                    });
                    setArtistMsg("Order saved successfully!");
                } catch (e) {
                    setArtistMsg("Failed to save order.");
                }
                }}
                itemLabel="Artist"
                msg={artistMsg}
                setMsg={setArtistMsg}
                idKey="_id"
                labelKeys={["name", "title", "_id"]}
                renderItem={(artist) => (
                <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Avatar
                    src={artist.profileImg?.filePath}
                    alt={artist.firstName}
                    sx={{ width: 48, height: 48, mr: 1 }}
                    />
                    <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontWeight={600}>
                        {artist.firstName} {artist.lastName}
                        </Typography>
                        {artist.isVerified && (
                        <CheckCircleIcon fontSize="small" color="primary" titleAccess="Verified" />
                        )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Followers: {artist.followers} &nbsp;|&nbsp; Balance: {artist.accountBalance?.toLocaleString(undefined, { maximumFractionDigits: 2 })} HBAR
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {artist.bio}
                    </Typography>
                    </Box>
                </Box>
                )}
            />
            </Box>

           {/* Right: Search/Add Artist */}
            <Box
            flex={1}
            minWidth={280}
            maxWidth={360}
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center", // Centers all child elements horizontally
                textAlign: "center",
                marginLeft: "auto",
                marginRight: "auto",
                gap: 2, // Adds consistent spacing between elements
            }}
            >
            <Typography className="sectionTitle">Search & Add Artist</Typography>
            <TextField
                className="textField"
                label="Artist ID or Name"
                value={artistId}
                onChange={(e) => setArtistId(e.target.value)}
                fullWidth
            sx={{ mb: 2 }} // Adds spacing below the TextField
            />
            <Button
                className="actionButton outlined"
                variant="outlined"
                onClick={() => handleArtistSearch(artistId)}
                fullWidth
                sx={{ mb: 2 }}
            >
                Search
            </Button>
            <List className="listRoot">
                {artistSearchResults.map((artist) => (
                <ListItem
                    button
                    className={artistId === artist.uId ? "selectedItem" : "listItem"}
                    key={artist.uId}
                    onClick={() => handleFeatureArtist(artist.uId)}
                    selected={artistId === artist.uId}
                >
                    {artist.displayArtistName || artist.fullName || artist.uId}
                </ListItem>
                ))}
            </List>
            <Button
                className="actionButton contained"
                variant="contained"
                onClick={handleFeatureArtist}
                fullWidth
                sx={{ mt: 1 }}
            >
                Feature
            </Button>
            <Typography className="statusMsg" color="primary">
                {artistMsg}
            </Typography>
            </Box>
        </Box>
        )}

            {/* Featured Music Tab */}
        {tab === 1 && (
        <Box className="tabPanel" sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {/* Left: Currently Featured Music */}
            <Box flex={1} minWidth={320} maxWidth={480}>
            <Typography className="sectionTitle">Currently Featured Music</Typography>
            <ReorderableFeaturedList
                items={featuredMusic}
                setItems={setFeaturedMusic}
                onRemove={handleRemoveFeaturedMusic}
                onSaveOrder={async () => {
                try {
                    await ApiService.ADMIN_FEATURE_MUSIC({
                    order: featuredMusic.map((music, idx) => ({
                        musicId: music._id,
                        orderNo: idx + 1,
                    })),
                    });
                    setMusicMsg("Order saved successfully!");
                } catch (e) {
                    setMusicMsg("Failed to save order.");
                }
                }}
                itemLabel="Music"
                msg={musicMsg}
                setMsg={setMusicMsg}
                idKey="_id"
                labelKeys={["title", "artist", "_id"]}
                renderItem={(music) => (
                <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Avatar
                    src={music.thumbnailImg?.filePath}
                    alt={music.title}
                    sx={{ width: 48, height: 48, mr: 1 }}
                    />
                    <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontWeight={600}>{music.title}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Artist: {music.uploader?.firstName || "Unknown"} {music.uploader?.lastName || ""}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Views: {music.views} | Listens: {music.listenCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Release Date: {new Date(music.releaseDate).toLocaleDateString()}
                    </Typography>
                    </Box>
                </Box>
                )}
            />
            </Box>

            {/* Right: Search/Add Music */}
            <Box
            flex={1}
            minWidth={280}
            maxWidth={360}
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                marginLeft: "auto",
                marginRight: "auto",
                gap: 2,
            }}
            >
            <Typography className="sectionTitle">Search & Add Music</Typography>
            <TextField
                className="textField"
                label="Music ID or Name"
                value={musicSearch}
                onChange={(e) => setMusicSearch(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
            />
            <Button
                className="actionButton outlined"
                variant="outlined"
                onClick={handleMusicSearch}
                fullWidth
                sx={{ mb: 2 }}
            >
                Search
            </Button>
            <List className="listRoot">
                {musicSearchResults.map((music) => (
                <ListItem
                    button
                    className={musicId === music._id ? "selectedItem" : "listItem"}
                    key={music._id}
                    onClick={() => setMusicId(music._id)}
                    selected={musicId === music._id}
                >
                    {music.title || music.name || music._id}
                </ListItem>
                ))}
            </List>
            <Button
                className="actionButton contained"
                variant="contained"
                onClick={handleFeatureMusic}
                fullWidth
                sx={{ mt: 1 }}
            >
                Feature
            </Button>
            <Typography className="statusMsg" color="primary">{musicMsg}</Typography>
            </Box>
        </Box>
        )}

      {/* Featured Video Tab */}
      {tab === 2 && (
        <Box className="tabPanel" sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {/* Left: Currently Featured Videos */}
          <Box flex={1} minWidth={320} maxWidth={480}>
            <Typography className="sectionTitle">Currently Featured Videos</Typography>
            <ReorderableFeaturedList
              items={featuredVideos}
              setItems={setFeaturedVideos}
              onRemove={handleRemoveFeaturedVideo}
              onSaveOrder={async () => {
                try {
                  await ApiService.ADMIN_FEATURE_VIDEO({
                    order: featuredVideos.map((video, idx) => ({
                      _id: video._id,
                      orderNo: idx + 1,
                    })),
                  });
                  setVideoMsg("Order saved successfully!");
                } catch (e) {
                  setVideoMsg("Failed to save order.");
                }
              }}
              itemLabel="Video"
              msg={videoMsg}
              setMsg={setVideoMsg}
              idKey="_id"
              labelKeys={["title", "uploader.displayArtistName", "_id"]}
              renderItem={(video) => (
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Avatar
                    src={video.thumbnail?.url}
                    alt={video.title}
                    sx={{ width: 48, height: 48, mr: 1 }}
                  />
                  <Box flex={1}>
                    <Typography fontWeight={600}>{video.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Uploaded By: {video.uploader?.displayArtistName || "Unknown"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Views: {video.views || 0} | Duration: {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Box>

          {/* Right: Search/Add Video */}
          <Box
            flex={1}
            minWidth={280}
            maxWidth={360}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              marginLeft: "auto",
              marginRight: "auto",
              gap: 2,
            }}
          >
            <Typography className="sectionTitle">Search & Add Video</Typography>
            <TextField
              className="textField"
              label="Video ID or Title"
              value={videoSearch}
              onChange={(e) => setVideoSearch(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Button
              className="actionButton outlined"
              variant="outlined"
              onClick={handleVideoSearch}
              fullWidth
              sx={{ mb: 2 }}
            >
              Search
            </Button>
            <List className="listRoot">
              {videoSearchResults.map((video) => (
                <ListItem
                  button
                  className={videoId === video._id ? "selectedItem" : "listItem"}
                  key={video._id}
                  onClick={() => setVideoId(video._id)}
                  selected={videoId === video._id}
                >
                  {video.title || video._id}
                </ListItem>
              ))}
            </List>
            <Button
              className="actionButton contained"
              variant="contained"
              onClick={handleFeatureVideo}
              fullWidth
              sx={{ mt: 1 }}
            >
              Feature
            </Button>
            <Typography className="statusMsg" color="primary">{videoMsg}</Typography>
          </Box>
        </Box>
      )}

{/* Featured Playlist Tab */}
{tab === 3 && (
  <Box className="tabPanel" sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
    {/* Left: Currently Featured Playlists */}
    <Box flex={1} minWidth={320} maxWidth={480}>
      <Typography className="sectionTitle">Currently Featured Playlists</Typography>
      <ReorderableFeaturedList
        items={featuredPlaylists}
        setItems={setFeaturedPlaylists}
        onRemove={handleRemoveFeaturedPlaylist}
        onSaveOrder={async () => {
          try {
            await ApiService.ADMIN_FEATURE_PLAYLIST({
              order: featuredPlaylists.map((playlist, idx) => ({
                playlistId: playlist._id,
                orderNo: idx + 1,
              })),
            });
            setPlaylistMsg("Order saved successfully!");
          } catch (e) {
            setPlaylistMsg("Failed to save order.");
          }
        }}
        itemLabel="Playlist"
        msg={playlistMsg}
        setMsg={setPlaylistMsg}
        idKey="_id"
        labelKeys={["title", "createdByDetails.displayArtistName", "_id"]}
        renderItem={(playlist) => (
          <Box display="flex" alignItems="center" gap={2} width="100%">
            <Avatar
              src={playlist.thumbnailImg?.filePath}
              alt={playlist.title}
              sx={{ width: 48, height: 48, mr: 1 }}
            />
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontWeight={600}>{playlist.title}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Created By: {playlist.createdByDetails?.displayArtistName || "Unknown"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Views: {playlist.views} | Streams: {Math.round(playlist.streamCount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Platform: {playlist.platform || "Unknown"}
              </Typography>

            </Box>
          </Box>
        )}
      />
    </Box>

    {/* Right: Search/Add Playlist */}
    <Box
      flex={1}
      minWidth={280}
      maxWidth={360}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        marginLeft: "auto",
        marginRight: "auto",
        gap: 2,
      }}
    >
      <Typography className="sectionTitle">Search & Add Playlist</Typography>
      <TextField
        className="textField"
        label="Playlist ID or Name"
        value={playlistSearch}
        onChange={(e) => setPlaylistSearch(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <Button
        className="actionButton outlined"
        variant="outlined"
        onClick={handlePlaylistSearch}
        fullWidth
        sx={{ mb: 2 }}
      >
        Search
      </Button>
      <List className="listRoot">
        {playlistSearchResults.map((playlist) => (
          <ListItem
            button
            className={playlistId === playlist._id ? "selectedItem" : "listItem"}
            key={playlist._id}
            onClick={() => setPlaylistId(playlist._id)}
            selected={playlistId === playlist._id}
          >
            {playlist.title || playlist.name || playlist._id}
          </ListItem>
        ))}
      </List>
      <Button
        className="actionButton contained"
        variant="contained"
        onClick={handleFeaturePlaylist}
        fullWidth
        sx={{ mt: 1 }}
      >
        Feature
      </Button>
      <Typography className="statusMsg" color="primary">{playlistMsg}</Typography>
    </Box>
  </Box>
)}
    </Box>
  );
}